'use client'

import { useState, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import cn from 'classnames'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { ExtraQuantities } from '@booking/types/booking'

import MinusIcon from '@booking/stages/QuantitySelection/components/TicketType/minus-icon.svg'
import PlusIcon from '@booking/stages/QuantitySelection/components/TicketType/plus-icon.svg'

import styles from './index.module.scss'
import { DAYS } from '../rock-in-rio-days'
import type { DayConfig } from '../rock-in-rio-days'
import posterImage from './rock-in-rio-poster-2.jpg'

// ---------------------------------------------------------------------------

export default function RockInRioExtrasGrid() {
  const state = useBookingState()
  const { dispatch } = useBookingActionsCtx()

  const extrasTotal = state.extras.reduce((sum, e) => sum + e.quantity, 0)
  const maxExtrasTotal = state.config.extras?.maxExtrasTotal
  const totalMaxReached = typeof maxExtrasTotal === 'number' && extrasTotal >= maxExtrasTotal

  const setExtraValue = useCallback(
    (category: string, nextQty: number) => {
      const existing = state.extras.find((e) => e.category === category)
      let next: ExtraQuantities

      if (existing) {
        next = state.extras.map((e) => (e.category === category ? { ...e, quantity: nextQty } : e))
      } else {
        next = [...state.extras, { category, quantity: nextQty }]
      }

      dispatch({ type: 'SET_EXTRAS', extras: next })
    },
    [state.extras, dispatch],
  )

  return (
    <div className={styles.grid}>
      {DAYS.map((day) => {
        const value = state.extras.find((e) => e.category === day.category)?.quantity ?? 0
        return (
          <DayCard
            key={day.category}
            day={day}
            value={value}
            totalMaxReached={totalMaxReached}
            onChange={(next) => setExtraValue(day.category, next)}
          />
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------

type DayCardProps = {
  day: DayConfig
  value: number
  totalMaxReached: boolean
  onChange: (next: number) => void
}

function DayCard({ day, value, totalMaxReached, onChange }: DayCardProps) {
  const t = useTranslations('booking')

  return (
    <div className={cn(styles.card, day.disabled && styles.cardDisabled)}>
      <div
        className={styles.cardHeader}
        style={day.gradient ? { background: day.gradient } : undefined}
      >
        <span className={styles.datePill}>{day.date}</span>
        <strong className={styles.headliner}>{day.headliner}</strong>
        <div className={styles.acts}>
          {day.acts.map((act, i) => (
            <span key={i} className={styles.act}>
              {act}
            </span>
          ))}
        </div>
        {day.disabled && (
          <div className={styles.soldOutOverlay}>
            <span className={styles.soldOutLabel}>{t('extras.soldOut')}</span>
          </div>
        )}
      </div>

      <div className={styles.cardBody}>
        {day.tagKey && <span className={styles.tag}>{t(day.tagKey)}</span>}
        <strong className={styles.title}>{t(day.titleKey)}</strong>
        {day.price && (
          <span className={styles.price}>
            <span className={cn(day.discountPrice && 'line-through')}>{day.price}</span>
            {day.discountPrice ? <span> {day.discountPrice} </span> : <></>}
            {t('extras.perPerson')}
          </span>
        )}
        {day.moreInfoKey && <MoreInfoPopover contentKey={day.moreInfoKey} />}
      </div>

      <div className={styles.stepper}>
        <div className={styles.stepperInner}>
          <button
            type="button"
            className={styles.stepperBtn}
            disabled={value <= 0}
            onClick={() => onChange(value - 1)}
            aria-label={`Decrease ${day.category}`}
          >
            <MinusIcon />
          </button>
          <span className={styles.stepperValue}>{value}</span>
          <button
            type="button"
            className={styles.stepperBtn}
            disabled={totalMaxReached}
            onClick={() => onChange(value + 1)}
            aria-label={`Increase ${day.category}`}
          >
            <PlusIcon />
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

function MoreInfoPopover({ contentKey }: { contentKey: string }) {
  const t = useTranslations('booking')
  const [open, setOpen] = useState(false)

  return (
    <>
      <button type="button" className={styles.moreInfoTrigger} onClick={() => setOpen(true)}>
        {t('extras.moreInfo')}
      </button>

      {open && (
        <div className={styles.modalOverlay} onClick={() => setOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={posterImage.src}
              alt={t('extras.rockInRio.posterAlt')}
              className={styles.modalImage}
            />
          </div>
        </div>
      )}
    </>
  )
}
